import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Badge, ErrorState, LoadingState, SeverityBadge, describeError, useConfirm } from '@/components/primitives';
import useResource from '@/pages/dashboard/useResource';
import { API, apiGet, authHeaders } from '@/pages/dashboard/api';
import { ago } from '@/pages/dashboard/format';

/*
 * People to protect: executives and high-risk staff the organization chooses to watch. We show where threat
 * reporting names them and whether their work email appears in breaches. No social network is searched.
 */

const GROUPS = [['executive', 'Executive'], ['finance', 'Finance'], ['hr', 'HR'], ['it', 'IT and security'], ['other', 'Other']];
const GROUP_LABEL = Object.fromEntries(GROUPS);
const EMPTY = { name: '', title: '', group: 'executive', emails: '' };

function PersonForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial);
  const submit = (e) => {
    e.preventDefault();
    onSave({ name: f.name, title: f.title || null, group: f.group, emails: f.emails.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean) });
  };
  return (
    <form className="br-card br-person-form" onSubmit={submit} data-testid="person-form">
      <h2 className="br-h2">{initial.id ? 'Edit person' : 'Add a person'}</h2>
      <div className="br-form-grid">
        <label className="br-field"><span>Full name</span><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} maxLength={80} required placeholder="Sara Al-Harbi" data-testid="person-name" /></label>
        <label className="br-field"><span>Job title</span><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} maxLength={80} placeholder="Chief Financial Officer" /></label>
        <label className="br-field"><span>Group</span>
          <select value={f.group} onChange={(e) => setF({ ...f, group: e.target.value })}>{GROUPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
        <label className="br-field"><span>Work email, up to three</span><input value={f.emails} onChange={(e) => setF({ ...f, emails: e.target.value })} placeholder="sara@yourcompany.com" data-testid="person-emails" /></label>
      </div>
      <div className="br-form-actions">
        <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={onCancel}>Cancel</button>
        <button type="submit" className="is-btn is-btn--primary is-btn--sm" disabled={saving || f.name.trim().length < 5} data-testid="save-person">{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </form>
  );
}

function Person({ p, onEdit, onDelete, onMonitor }) {
  const [open, setOpen] = useState(false);
  const worst = p.exposure.reduce((n, e) => n + e.passwords_exposed, 0);
  const breaches = p.exposure.reduce((n, e) => n + e.breaches, 0);
  return (
    <li className="br-card br-person" data-testid={`person-${p.id}`}>
      <div className="br-person-h">
        <div className="min-w-0">
          <h3 className="br-person-name">{p.name}</h3>
          <p className="br-muted">{[p.title, GROUP_LABEL[p.group]].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="br-person-actions">
          <button type="button" className="br-icon-btn" onClick={() => onEdit(p)} aria-label={`Edit ${p.name}`}><Pencil aria-hidden="true" /></button>
          <button type="button" className="br-icon-btn" onClick={() => onDelete(p)} aria-label={`Remove ${p.name}`}><Trash2 aria-hidden="true" /></button>
        </div>
      </div>
      <div className="br-person-grid">
        <div>
          <h4 className="br-h3">Email exposure</h4>
          {!p.exposure.length ? <p className="br-muted">No address given. Add a work email to see breach exposure.</p> : (
            <ul className="br-exposure">
              {p.exposure.map((e) => (
                <li key={e.email}>
                  <span className="br-mono">{e.email}</span>
                  {!e.monitored ? (
                    <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => onMonitor(e.email)}><Plus aria-hidden="true" /> Monitor for breaches</button>
                  ) : e.breaches ? (
                    <span className="br-exp-bad">{e.breaches} {e.breaches === 1 ? 'breach' : 'breaches'}{e.passwords_exposed ? `, ${e.passwords_exposed} with a password` : ''}</span>
                  ) : <Badge size="sm" tone="success">No breaches</Badge>}
                </li>
              ))}
            </ul>
          )}
          {worst > 0 && <p className="br-warn">A password for this person has been exposed. <Link to="/exposure/credentials">Review it in Credentials</Link>.</p>}
          {breaches > 0 && worst === 0 && <p className="br-fine"><Link to="/exposure/credentials">Review in Credentials</Link></p>}
        </div>
        <div>
          <h4 className="br-h3">In threat reporting</h4>
          {p.mentions.count === 0 ? <p className="br-muted">No threat report names this person.</p> : (
            <>
              <p>{p.mentions.count} {p.mentions.count === 1 ? 'report names' : 'reports name'} them.{' '}
                <button type="button" className="br-linkish" onClick={() => setOpen(!open)} aria-expanded={open}>{open ? 'Hide' : 'Show'}</button></p>
              {open && (
                <ul className="br-mentions">
                  {p.mentions.items.map((m) => (
                    <li key={m.id}>
                      <SeverityBadge severity={m.severity} size="sm" />
                      <span>
                        {m.yours ? <Link to={`/threats?id=${encodeURIComponent(m.id)}`}>{m.name}</Link> : <span>{m.name}</span>}
                        <span className="br-muted"> {ago(m.discovered_at)}{m.source_url ? <> · <a href={m.source_url} target="_blank" rel="noopener noreferrer">source <ExternalLink aria-hidden="true" /></a></> : null}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </li>
  );
}

export default function People() {
  const res = useResource(() => apiGet('/brand/people'), [], { poll: false });
  const [editing, setEditing] = useState(null); // null | EMPTY | person
  const [saving, setSaving] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const d = res.data;

  const save = async (body) => {
    setSaving(true);
    try {
      if (editing.id) await axios.put(`${API}/brand/people/${editing.id}`, body, { headers: authHeaders() });
      else await axios.post(`${API}/brand/people`, body, { headers: authHeaders() });
      await res.reload();
      toast.success(editing.id ? 'Saved' : `${body.name} added`);
      setEditing(null);
    } catch (err) {
      toast.error("Couldn't save", { description: describeError(err) });
    } finally {
      setSaving(false);
    }
  };
  const remove = async (p) => {
    const ok = await confirm({ title: `Remove ${p.name}?`, description: 'They are removed from this list. Their email stays in breach monitoring if you added it there.', confirmLabel: 'Remove', destructive: true });
    if (!ok) return;
    try { await axios.delete(`${API}/brand/people/${p.id}`, { headers: authHeaders() }); await res.reload(); toast.success('Removed'); } catch (err) { toast.error("Couldn't remove", { description: describeError(err) }); }
  };
  const monitor = async (email) => {
    try { await axios.post(`${API}/emails/register`, { email }, { headers: authHeaders() }); await res.reload(); toast.success(`${email} is now monitored`, { description: 'Use Check in Credentials to run the first check.' }); }
    catch (err) { toast.error("Couldn't start monitoring", { description: describeError(err) }); }
  };
  const startEdit = (p) => setEditing({ id: p.id, name: p.name, title: p.title || '', group: p.group, emails: (p.emails || []).join(', ') });

  return (
    <div className="br-tab" data-testid="brand-people">
      <div className="br-toolbar">
        <p className="br-fine">Executives and high-risk staff you want watched, because attackers impersonate them to move money or steal logins. {d?.note}</p>
        <div className="br-toolbar-actions">
          <button type="button" className="is-btn is-btn--primary is-btn--sm" onClick={() => setEditing(EMPTY)} disabled={!!editing || (d && d.people.length >= d.limit)} data-testid="add-person"><Plus aria-hidden="true" /> Add a person</button>
        </div>
      </div>
      {editing && <PersonForm initial={editing} onSave={save} onCancel={() => setEditing(null)} saving={saving} />}
      {res.loading && !d ? <LoadingState label="people" rows={3} />
        : res.error && !d ? <ErrorState title="Couldn't load people" error={res.error} onRetry={res.reload} />
          : !d.people.length && !editing ? <div className="br-empty" data-testid="people-empty"><b>No one is on the list yet</b><span>Add your executives and anyone in finance or HR who handles payments or sensitive data. We show where threat reporting names them and whether their work email has been in a breach.</span></div>
            : <ul className="br-people">{d.people.map((p) => <Person key={p.id} p={p} onEdit={startEdit} onDelete={remove} onMonitor={monitor} />)}</ul>}
      {d && d.people.length > 0 && <p className="br-fine">{d.people.length} of {d.limit} people. Removing a person deletes their entry here.</p>}
      {confirmDialog}
    </div>
  );
}
